import { useCallback, useMemo, type Dispatch, type ReactNode, type SetStateAction } from 'react';
import noop from 'lodash/noop';
import { TextSmRg } from '@/UI/AppTypography/AppTypography';
import { Dropdown, type Option } from '@/UI/Dropdown/Dropdown';
import { Flex } from '@/UI/styles';
import { color } from '@/colors';
import { I18N_KEYS } from '@/language/keys';
import { encodeFontStyleValue, type FontStyleCombination, type MissingCustomFontFace } from '@/utils/customFonts.utils';
import { dropdownTriggerSx, modalOutline, rowGrid } from './missingWorkflowFontsModal.styles';
import type { TFunction } from 'i18next';
import type { BuiltInFont } from 'web';

function weightToI18nKey(weight: number): string {
  switch (weight) {
    case 100:
      return I18N_KEYS.COMPOSITOR.LAYER_PROPERTY_PANEL.FONT_WEIGHTS.THIN;
    case 200:
      return I18N_KEYS.COMPOSITOR.LAYER_PROPERTY_PANEL.FONT_WEIGHTS.EXTRA_LIGHT;
    case 300:
      return I18N_KEYS.COMPOSITOR.LAYER_PROPERTY_PANEL.FONT_WEIGHTS.LIGHT;
    case 400:
      return I18N_KEYS.COMPOSITOR.LAYER_PROPERTY_PANEL.FONT_WEIGHTS.REGULAR;
    case 500:
      return I18N_KEYS.COMPOSITOR.LAYER_PROPERTY_PANEL.FONT_WEIGHTS.MEDIUM;
    case 600:
      return I18N_KEYS.COMPOSITOR.LAYER_PROPERTY_PANEL.FONT_WEIGHTS.SEMI_BOLD;
    case 700:
      return I18N_KEYS.COMPOSITOR.LAYER_PROPERTY_PANEL.FONT_WEIGHTS.BOLD;
    case 800:
      return I18N_KEYS.COMPOSITOR.LAYER_PROPERTY_PANEL.FONT_WEIGHTS.EXTRA_BOLD;
    case 900:
      return I18N_KEYS.COMPOSITOR.LAYER_PROPERTY_PANEL.FONT_WEIGHTS.BLACK;
    default:
      return I18N_KEYS.COMPOSITOR.LAYER_PROPERTY_PANEL.FONT_WEIGHTS.REGULAR;
  }
}

function sourceStyleLabel(t: TFunction, weight: number, isItalic: boolean): string {
  const w = t(weightToI18nKey(weight));
  if (isItalic) {
    return `${w} ${t(I18N_KEYS.COMPOSITOR.LAYER_PROPERTY_PANEL.FONT_STYLES.ITALIC)}`;
  }
  return w;
}

function replacementStyleLabel(t: TFunction, combo: FontStyleCombination): string {
  return sourceStyleLabel(t, combo.weight, combo.isItalic);
}

type MissingFontFaceReplacementPickersProps = {
  replacementFont: BuiltInFont | null;
  styleValue: string | null;
  /** Built from the current replacement font’s available weight/italic pairs. */
  styleOptions: Option<string>[];
  /** No font, no styles, or exactly one auto-filled style — style dropdown hidden/disabled. */
  styleSelectDisabled: boolean;
  fontOptions: Option<BuiltInFont>[];
  emptyFontPlaceholder: ReactNode;
  emptyStylePlaceholder: ReactNode;
  noOptionsText: string;
  onReplacementFontChange: (font: BuiltInFont) => void;
  onReplacementStyleChange: (encodedStyle: string) => void;
};

/** Per-face replacement font + variant; family header can set a default, but each row can diverge. */
function MissingFontFaceReplacementPickers({
  replacementFont,
  styleValue,
  styleOptions,
  styleSelectDisabled,
  fontOptions,
  emptyFontPlaceholder,
  emptyStylePlaceholder,
  noOptionsText,
  onReplacementFontChange,
  onReplacementStyleChange,
}: MissingFontFaceReplacementPickersProps) {
  const handleReplacementFontChange = useCallback(
    (opt: Option<BuiltInFont>) => {
      onReplacementFontChange(opt.value);
    },
    [onReplacementFontChange],
  );

  const handleReplacementStyleChange = useCallback(
    (opt: Option<string>) => {
      onReplacementStyleChange(opt.value);
    },
    [onReplacementStyleChange],
  );

  return (
    <Flex sx={{ gap: 0.5, minWidth: 0, alignItems: 'center', width: '100%' }}>
      <Flex sx={{ flex: 1, minWidth: 0 }}>
        <Dropdown
          disabled={false}
          value={replacementFont}
          confirmedValue={replacementFont}
          options={fontOptions}
          onChange={handleReplacementFontChange}
          onClose={noop}
          onOpen={noop}
          search
          matchTriggerWidth
          width="100%"
          size="small"
          sx={dropdownTriggerSx}
          emptyState={emptyFontPlaceholder}
          noOptionsText={noOptionsText}
        />
      </Flex>
      <Flex sx={{ flex: 1, minWidth: 0 }}>
        <Dropdown
          disabled={styleSelectDisabled}
          value={styleValue}
          confirmedValue={styleValue}
          options={styleOptions}
          onChange={handleReplacementStyleChange}
          onClose={noop}
          onOpen={noop}
          search={false}
          matchTriggerWidth
          width="100%"
          size="small"
          sx={dropdownTriggerSx}
          emptyState={emptyStylePlaceholder}
        />
      </Flex>
    </Flex>
  );
}

export type MissingFontFaceAccordionItemRowProps = {
  face: MissingCustomFontFace;
  faceFont: Record<string, BuiltInFont>;
  faceStyleValue: Record<string, string>;
  setFaceFont: Dispatch<SetStateAction<Record<string, BuiltInFont>>>;
  setFaceStyleValue: Dispatch<SetStateAction<Record<string, string>>>;
  combosByFontFamily: Map<BuiltInFont, FontStyleCombination[]>;
  fontOptions: Option<BuiltInFont>[];
  emptyFontPlaceholder: ReactNode;
  emptyStylePlaceholder: ReactNode;
  noOptionsText: string;
  t: TFunction;
};

/** One expanded row: source style (with chevron-column alignment) → replacement controls. */
export function MissingFontFaceAccordionItemRow({
  face,
  faceFont,
  faceStyleValue,
  setFaceFont,
  setFaceStyleValue,
  combosByFontFamily,
  fontOptions,
  emptyFontPlaceholder,
  emptyStylePlaceholder,
  noOptionsText,
  t,
}: MissingFontFaceAccordionItemRowProps) {
  const sourceLabel = useMemo(() => sourceStyleLabel(t, face.weight, face.isItalic), [t, face.weight, face.isItalic]);

  const b = faceFont[face.faceKey];
  const combos = b ? combosByFontFamily.get(b) : undefined;

  const styleOpts = useMemo<Option<string>[]>(
    () =>
      combos?.map((c) => ({
        value: encodeFontStyleValue(c.weight, c.isItalic),
        label: replacementStyleLabel(t, c),
        id: encodeFontStyleValue(c.weight, c.isItalic),
      })) ?? [],
    [combos, t],
  );

  const styleVal = faceStyleValue[face.faceKey] ?? null;
  const styleSelectDisabled = !b || !combos || combos.length <= 1;

  const onReplacementFontChange = useCallback(
    (v: BuiltInFont) => {
      setFaceFont((p) => ({ ...p, [face.faceKey]: v }));
      setFaceStyleValue((p) => {
        const n = { ...p };
        delete n[face.faceKey];
        return n;
      });
    },
    [face.faceKey, setFaceFont, setFaceStyleValue],
  );

  const onReplacementStyleChange = useCallback(
    (encoded: string) => {
      setFaceStyleValue((p) => ({ ...p, [face.faceKey]: encoded }));
    },
    [face.faceKey, setFaceStyleValue],
  );

  return (
    <Flex
      sx={{
        ...rowGrid,
        width: '100%',
        py: 0.5,
        minHeight: 40,
        boxSizing: 'border-box',
        borderTop: modalOutline,
      }}
    >
      <Flex sx={{ alignItems: 'center', gap: 0.5, minWidth: 0, pl: 0 }}>
        {/* Invisible spacer matches the chevron column so source labels line up with the family row above. */}
        <Flex
          aria-hidden
          sx={{
            width: 12,
            height: 12,
            flexShrink: 0,
            visibility: 'hidden',
          }}
        />
        <TextSmRg sx={{ color: color.White100, overflow: 'hidden', textOverflow: 'ellipsis', minWidth: 0 }}>
          {sourceLabel}
        </TextSmRg>
      </Flex>
      <TextSmRg
        component="span"
        sx={{
          color: color.White16_T,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          width: 16,
          userSelect: 'none',
        }}
      >
        →
      </TextSmRg>
      <MissingFontFaceReplacementPickers
        replacementFont={b ?? null}
        styleValue={styleVal}
        styleOptions={styleOpts}
        styleSelectDisabled={styleSelectDisabled}
        fontOptions={fontOptions}
        emptyFontPlaceholder={emptyFontPlaceholder}
        emptyStylePlaceholder={emptyStylePlaceholder}
        noOptionsText={noOptionsText}
        onReplacementFontChange={onReplacementFontChange}
        onReplacementStyleChange={onReplacementStyleChange}
      />
    </Flex>
  );
}
